import React, { Component } from 'react';
import { bool, func, object, string } from 'prop-types';
import { Form as FinalForm, Field } from 'react-final-form';
import classNames from 'classnames';
import { intlShape, injectIntl } from '../../../../util/reactIntl';
import { Form, LocationAutocompleteInput } from '../../../../components';
import IconSearchDesktop from './IconSearchDesktop';
import css from './TopbarSearchForm.module.css';
import { SearchType } from '../../../../util/enums';

const identity = v => v;

const KeywordSearchField = props => {
  const { keywordSearchWrapperClasses, iconClass, intl, isMobile, inputRef } = props;
  return (
    <div className={keywordSearchWrapperClasses}>
      <button className={css.searchSubmit}>
        <div className={iconClass}>
          <IconSearchDesktop />
        </div>
      </button>
      <Field
        name="keywords"
        render={({ input, meta }) => {
          return (
            <input
              className={isMobile ? css.mobileInput : css.desktopInput}
              {...input}
              id={isMobile ? 'keyword-search-mobile' : 'keyword-search'}
              data-testid={isMobile ? 'keyword-search-mobile' : 'keyword-search'}
              ref={inputRef}
              type="text"
              placeholder={intl.formatMessage({
                id: 'TopbarSearchForm.keywordPlaceholder',
              })}
              autoComplete="off"
              value={input.value || ''}
            />
          );
        }}
      />
    </div>
  );
};

const LocationSearchField = props => {
  const { desktopInputRootClass, intl, isMobile, inputRef, onLocationChange } = props;
  return (
    <Field
      name="location"
      format={identity}
      render={({ input, meta }) => {
        const { onChange, ...restInput } = input;

        const searchOnChange = value => {
          onChange(value);
          onLocationChange(value);
        };

        return (
          <LocationAutocompleteInput
            className={isMobile ? css.mobileInputRoot : desktopInputRootClass}
            iconClassName={isMobile ? css.mobileIcon : css.desktopIcon}
            inputClassName={isMobile ? css.mobileInput : css.desktopInput}
            predictionsClassName={isMobile ? css.mobilePredictions : css.desktopPredictions}
            predictionsAttributionClassName={isMobile ? css.mobilePredictionsAttribution : null}
            placeholder={intl.formatMessage({ id: 'TopbarSearchForm.locationPlaceholder' })}
            closeOnBlur={!isMobile}
            inputRef={inputRef}
            input={{ ...restInput, onChange: searchOnChange }}
            meta={meta}
          />
        );
      }}
    />
  );
};

class TopbarSearchFormComponent extends Component {
  constructor(props) {
    super(props);
    this.state = {
      searchType:
        props.appConfig.search.mainSearch === SearchType.KEYWORDS
          ? SearchType.KEYWORDS
          : SearchType.LOCATION,
      submitInProgress: false,
    };
    this.submitCalledRef = React.createRef();
    this.submitCalledRef.current = false;

    this.onChange = this.onChange.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.handleSearchTypeChange = this.handleSearchTypeChange.bind(this);

    this.searchInput = null;
    this.setSearchInputRef = element => {
      this.searchInput = element;
    };
  }

  handleSearchTypeChange(event) {
    this.setState({ searchType: event.target.value });
  }

  onChange(location) {
    const { onSubmit } = this.props;
    const { searchType } = this.state;
    const selectedPlace =
      location && typeof location === 'object' && location.selectedPlace ? location.selectedPlace : null;
    if (searchType === SearchType.LOCATION && selectedPlace) {
      onSubmit({ searchType: SearchType.LOCATION, location });
      this.searchInput?.blur();
    }
  }
  onSubmit(values) {
    const { onSubmit } = this.props;
    const { searchType } = this.state;
    if (searchType === SearchType.KEYWORDS) {
      onSubmit({ searchType: SearchType.KEYWORDS, keywords: values?.keywords || '' });
      this.searchInput?.blur();
    }
  }

  render() {
    const {
      rootClassName,
      className,
      desktopInputRoot,
      intl,
      isMobile,
      routeConfiguration,
    } = this.props;
    const { searchType, submitInProgress } = this.state;

    const classes = classNames(rootClassName, className);
    const desktopInputRootClass = desktopInputRoot || css.desktopInputRoot;

    const keywordSearchWrapperClasses = classNames(
      css.keywordSearchWrapper,
      isMobile ? css.mobileInputRoot : desktopInputRootClass
    );

    return (
      <FinalForm
        onSubmit={this.onSubmit}
        initialValues={{
          keywords: '',
          location: null,
        }}
        render={formRenderProps => {
          const { handleSubmit } = formRenderProps;
          const preventFormSubmit = e => e.preventDefault();
          const submitFormFn =
            searchType === SearchType.KEYWORDS ? handleSubmit : preventFormSubmit;

          return (
            <Form className={classes} onSubmit={submitFormFn} enforcePagePreloadFor="SearchPage">
              <div className={css.searchTypeWrapper}>
                <select
                  value={searchType}
                  onChange={this.handleSearchTypeChange}
                  className={css.searchTypeSelect}
                  aria-label={intl.formatMessage({ id: 'TopbarSearchForm.searchTypeLabel' })}
                  disabled={submitInProgress}
                >
                  <option value={SearchType.LOCATION}>
                    {intl.formatMessage({ id: 'TopbarSearchForm.locationOption' })}
                  </option>
                  <option value={SearchType.KEYWORDS}>
                    {intl.formatMessage({ id: 'TopbarSearchForm.keywordsOption' })}
                  </option>
                </select>
              </div>
              {searchType === SearchType.KEYWORDS ? (
                <KeywordSearchField
                  keywordSearchWrapperClasses={keywordSearchWrapperClasses}
                  iconClass={classNames(isMobile ? css.mobileIcon : css.desktopIcon || css.icon)}
                  intl={intl}
                  isMobile={isMobile}
                  inputRef={this.setSearchInputRef}
                />
              ) : (
                <LocationSearchField
                  desktopInputRootClass={desktopInputRootClass}
                  intl={intl}
                  isMobile={isMobile}
                  inputRef={this.setSearchInputRef}
                  onLocationChange={this.onChange}
                />
              )}
            </Form>
          );
        }}
      />
    );
  }
}

TopbarSearchFormComponent.defaultProps = {
  rootClassName: null,
  className: null,
  desktopInputRoot: null,
  isMobile: false,
  boatLength: null,
};

TopbarSearchFormComponent.propTypes = {
  rootClassName: string,
  className: string,
  desktopInputRoot: string,
  onSubmit: func.isRequired,
  isMobile: bool,
  appConfig: object.isRequired,
  boatLength: string,
  intl: intlShape.isRequired,
};

const TopbarSearchForm = injectIntl(TopbarSearchFormComponent);

export default TopbarSearchForm;
